import {
  CommercialMeterKey,
  CommercialPlanKey,
  type CommercialCatalogSnapshot,
  type CommercialPlanDefinition,
  type CommercialPlanSnapshot,
} from "@/modules/commercial-plans";

const FOCUSED_PLAN_COUNT = 3;
const FREE_FOCUSED_PLAN_COUNT = 4;

export interface CommercialPlanSelection {
  initialSelectedPlanKey: CommercialPlanKey;
  recommendedPlanKey: CommercialPlanKey;
  visiblePlans: readonly CommercialPlanDefinition[];
}

const getPlan = (
  catalog: CommercialCatalogSnapshot,
  planKey: string | null | undefined
): CommercialPlanDefinition | undefined => catalog.plans.find(plan => plan.key === planKey);

const getAvailablePlansAtOrAbove = ({
  catalog,
  currentPlan,
}: {
  catalog: CommercialCatalogSnapshot;
  currentPlan: CommercialPlanDefinition;
}): CommercialPlanDefinition[] =>
  catalog.plans
    .filter(plan => plan.position >= currentPlan.position)
    .sort((left, right) => left.position - right.position);

const getFirstPurchasablePlan = (
  plans: readonly CommercialPlanDefinition[],
  currentPlan: CommercialPlanDefinition
): CommercialPlanDefinition | undefined =>
  plans.find(plan => plan.position > currentPlan.position && plan.checkoutAvailable);

const getMeterRecommendation = ({
  currentPlan,
  meterKey,
  plans,
}: {
  currentPlan: CommercialPlanDefinition;
  meterKey: CommercialMeterKey | null;
  plans: readonly CommercialPlanDefinition[];
}): CommercialPlanDefinition | undefined => {
  if (!meterKey) {
    return undefined;
  }

  return plans.find(
    plan =>
      plan.position > currentPlan.position &&
      plan.checkoutAvailable &&
      plan.allowances[meterKey] > currentPlan.allowances[meterKey]
  );
};

const getNewCatalogRecommendation = ({
  catalog,
  currentPlan,
  meterKey,
  plans,
}: {
  catalog: CommercialCatalogSnapshot;
  currentPlan: CommercialPlanDefinition;
  meterKey: CommercialMeterKey | null;
  plans: readonly CommercialPlanDefinition[];
}): CommercialPlanDefinition => {
  if (currentPlan.key === CommercialPlanKey.Mem) {
    const proPlan = getPlan(catalog, CommercialPlanKey.MemPro);
    const preferredFreePlan = plans.find(
      plan => plan.checkoutAvailable && proPlan !== undefined && plan.position >= proPlan.position
    );
    if (preferredFreePlan) {
      return preferredFreePlan;
    }
  }

  return (
    getMeterRecommendation({ currentPlan, meterKey, plans }) ??
    getFirstPurchasablePlan(plans, currentPlan) ??
    currentPlan
  );
};

const getLegacyRecommendation = ({
  catalog,
  snapshot,
}: {
  catalog: CommercialCatalogSnapshot;
  snapshot: CommercialPlanSnapshot;
}): CommercialPlanDefinition =>
  getPlan(catalog, snapshot.migration?.recommendedPlanKey) ??
  getPlan(catalog, CommercialPlanKey.MemPro) ??
  catalog.plans[0];

export const getCommercialPlanSelection = ({
  catalog,
  meterKey = null,
  mode,
  snapshot,
}: {
  catalog: CommercialCatalogSnapshot;
  meterKey?: CommercialMeterKey | null;
  mode: "all" | "focused";
  snapshot: CommercialPlanSnapshot;
}): CommercialPlanSelection => {
  const currentPlan = getPlan(catalog, snapshot.currentPlan.key);

  if (!currentPlan) {
    const recommendation = getLegacyRecommendation({ catalog, snapshot });
    const eligiblePlans = catalog.plans
      .filter(plan => plan.position >= recommendation.position)
      .sort((left, right) => left.position - right.position);
    const visiblePlans =
      mode === "all" ? eligiblePlans : eligiblePlans.slice(0, FOCUSED_PLAN_COUNT);

    return {
      initialSelectedPlanKey: recommendation.key,
      recommendedPlanKey: recommendation.key,
      visiblePlans,
    };
  }

  const eligiblePlans = getAvailablePlansAtOrAbove({
    catalog,
    currentPlan,
  }).filter(plan => !snapshot.isTrial || plan.position > currentPlan.position);
  const recommendation = getNewCatalogRecommendation({
    catalog,
    currentPlan,
    meterKey,
    plans: eligiblePlans,
  });
  const focusedPlanCount =
    currentPlan.key === CommercialPlanKey.Mem ? FREE_FOCUSED_PLAN_COUNT : FOCUSED_PLAN_COUNT;

  return {
    initialSelectedPlanKey: recommendation.key,
    recommendedPlanKey: recommendation.key,
    visiblePlans: mode === "all" ? eligiblePlans : eligiblePlans.slice(0, focusedPlanCount),
  };
};
